// \src\pages\home\components\PromoCarouselHome.tsx

import { useRef } from "react";
import { Link } from "react-router-dom";
import { useAdmin } from "@/context/AdminContext";

function getLocalDateString(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function isProductActive(product: {
  active: boolean;
  activeUntil: string;
}): boolean {
  if (!product.active) {
    return false;
  }

  if (!product.activeUntil) {
    return true;
  }

  return product.activeUntil >= getLocalDateString();
}

function isPromotionActive(product: {
  promotionUntil: string;
}): boolean {
  if (!product.promotionUntil) {
    return true;
  }

  return product.promotionUntil >= getLocalDateString();
}

export default function PromoCarouselHome() {
  const { data } = useAdmin();
  const promoProducts = data.products.filter(
    (product) =>
      data.promotions.includes(product.id) &&
      isProductActive(product) &&
      isPromotionActive(product),
  );
  const trackRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = () => {
    if (trackRef.current) {
      trackRef.current.style.animationPlayState = "paused";
    }
  };

  const handleMouseLeave = () => {
    if (trackRef.current) {
      trackRef.current.style.animationPlayState = "running";
    }
  };

  const doubled = [...promoProducts, ...promoProducts];

  if (promoProducts.length === 0) return null;

  return (
    <section className="w-full py-14 bg-foreground-950 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 mb-8">
        <div className="flex items-center justify-between">
          <div>
            <span className="inline-block bg-primary-500/20 text-primary-400 text-xs font-label font-semibold uppercase tracking-widest px-3 py-1 rounded-full mb-2">
              Ofertas especiais
            </span>
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50">
              Promoções em Destaque
            </h2>
          </div>
          <Link
            to="/promocoes"
            className="hidden sm:flex items-center gap-2 text-primary-400 hover:text-primary-300 text-sm font-label font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            Ver todas
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>

      <div
        className="relative w-full overflow-hidden"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div
          ref={trackRef}
          className="flex gap-5 w-max promo-track animate-scroll-left"
          style={{ animationDuration: "25s", paddingLeft: "20px" }}
        >
          {doubled.map((product, i) => (
            <Link
              key={`${product.id}-${i}`}
              to={`/produto/${product.slug}`}
              className="shrink-0 w-52 bg-background-50 rounded-lg overflow-hidden border border-background-200 hover:border-primary-300 transition-all duration-300 cursor-pointer group"
            >
              <div className="relative h-40 overflow-hidden">
                <img
                  src={product.images[0]}
                  alt={product.name}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                {product.promoLabel && (
                  <span className="absolute top-2 left-2 bg-primary-500 text-background-50 text-xs font-label font-bold px-2 py-0.5 rounded">
                    {product.promoLabel}
                  </span>
                )}
              </div>
              <div className="p-3">
                <p className="text-foreground-900 font-label font-semibold text-xs leading-tight mb-1 line-clamp-2">{product.name}</p>
                {product.code && <span className="inline-flex items-center bg-primary-50 border border-primary-100 text-primary-700 text-[11px] font-label font-semibold px-2 py-0.5 rounded mt-1">Código: {product.code}</span>}
                <p className="text-foreground-500 text-xs mt-1">{product.brand}</p>
                {product.showPrice && (
                  <p className="text-primary-600 font-heading font-bold text-base">
                    R$ {product.price.toFixed(2).replace(".", ",")}
                  </p>
                )}
              </div>
            </Link>
          ))}
        </div>
      </div>

      <div className="text-center mt-6 sm:hidden">
        <Link
          to="/promocoes"
          className="inline-flex items-center gap-2 text-primary-400 text-sm font-label font-medium cursor-pointer"
        >
          Ver todas as promoções
          <i className="ri-arrow-right-line" />
        </Link>
      </div>
    </section>
  );
}